<script>
  import {getContext} from 'svelte';
  import {combineLatest} from 'rxjs';
  import {map} from 'rxjs/operators';
  import {PATHS} from '@/const/paths';
  import {coinUtil} from '@/helper/coin';
  import {fixLink$} from '@/store/lang';
  import {isMobileSize$} from '@/streams/window-inner-size';
  import {spotMarkets$, newListingMarkets$, contractMarkets$, topGainers$} from '@/modules/markets/streams';
  import Button from '@/widgets/Button.svelte';
  import TrendImage from '@/modules/home/components/TrendImage.svelte';
  import SkeletonBasicView from '@/widgets/skeleton/BasicView.svelte';
  import SkeletonLine from '@/widgets/skeleton/SkeletonLine.svelte';
  import {hotContracts$, hotSpots$} from '../streams';
  import cLang from '@/modules/copy-trading/Common.lang.js';
  import lang from '../HomePage.lang';

  const HOT_DATA_LIST_COUNT = 7;
  const MORE_DATA_LIST_COUNT = 3;
  const LG = getContext('LG');
  const more = [
    {
      label: LG(lang.TopGainers),
      cryptos: [],
      href: $fixLink$(PATHS.MARKETS.pathname) + '?tabType=Spot',
    },
    {
      label: LG(lang.NewListhings),
      cryptos: [],
      href: $fixLink$(PATHS.MARKETS.pathname) + '?tabType=NewListings',
    },
  ];
  const TAB_TYPE = {
    SPOT: 'SPOT',
    CONTRACT: 'CONTRACT',
    NEW_LISTING: 'NEW_LISTING',
    GAINER: 'GAINER',
  };
  const marketsMap = {
    SPOT: {
      marketsId: 0,
      sortKey: 'combinationTurnoverEv',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname),
    },
    CONTRACT: {
      marketsId: 2,
      sortKey: 'combinationTurnoverEv',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname) + '?tabType=Contract',
    },
    NEW_LISTING: {
      marketsId: 1,
      sortKey: 'listTime',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname + '?tabType=NewListings'),
    },
    GAINER: {
      marketsId: 0,
      sortKey: 'changePercent',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname + '?tabType=Spot&sort=changePercent'),
    },
  };

  let spotBackupList = [];
  let contractBackupList = [];

  const hotData$ = combineLatest([spotMarkets$, hotSpots$, contractMarkets$, hotContracts$, isMobileSize$, topGainers$, newListingMarkets$]).pipe(
    map(([spotMarkets, spots, contractMarkets, contracts, isMobileSize, topGainers, newListingMarkets]) => {
      let spotList = spots.map((s) => spotMarkets.find((m) => m.symbol === s)).filter(Boolean);
      let contractList = contracts.map((s) => contractMarkets.find((m) => m.symbol === s)).filter(Boolean);
      if (spotList.length < HOT_DATA_LIST_COUNT) {
        spotList = composeArray(spotList, spotBackupList).slice(0, HOT_DATA_LIST_COUNT);
      }
      if (contractList.length < HOT_DATA_LIST_COUNT) {
        contractList = composeArray(contractList, contractBackupList).slice(0, HOT_DATA_LIST_COUNT);
      }
      if (isMobileSize) {
        return {
          [TAB_TYPE.SPOT]: spotList,
          [TAB_TYPE.CONTRACT]: contractList,
          [TAB_TYPE.GAINER]: topGainers,
          [TAB_TYPE.NEW_LISTING]: newListingMarkets.slice(0, 3),
        };
      }
      return {
        [TAB_TYPE.SPOT]: spotList,
        [TAB_TYPE.CONTRACT]: contractList,
      };
    })
  );

  let activeTab = TAB_TYPE.SPOT;

  $: spotBackupList = genData(HOT_DATA_LIST_COUNT, TAB_TYPE.SPOT, $spotMarkets$, $newListingMarkets$, $contractMarkets$);
  $: contractBackupList = genData(HOT_DATA_LIST_COUNT, TAB_TYPE.CONTRACT, $spotMarkets$, $newListingMarkets$, $contractMarkets$);
  $: updateMore($topGainers$, $newListingMarkets$);

  function updateMore(topGainers, newListingMarkets) {
    more[0].cryptos = genData(MORE_DATA_LIST_COUNT, TAB_TYPE.GAINER, topGainers);
    more[1].cryptos = genData(MORE_DATA_LIST_COUNT, TAB_TYPE.NEW_LISTING, newListingMarkets);
  }

  function composeArray(list1, list2) {
    const ret = [];
    [...list1, ...list2].forEach((item) => {
      if (!ret.find(({symbol}) => item.symbol === symbol)) {
        ret.push(item);
      }
    });
    return ret;
  }

  function genData(length, tab, ...rest) {
    const map = marketsMap[tab];
    const markets = rest.length > 1 ? rest[map.marketsId] : rest[0];
    return markets ? markets.sort((a, b) => b[map.sortKey] - a[map.sortKey]).slice(0, length) : [];
  }

  function tabClickHandler(event) {
    const tab = event.target.dataset.tab;
    if (!tab) return;
    activeTab = tab;
  }

  function tradeClickHandler(data) {
    return function () {
      window.open(data.tradeLink, '_blank', 'noopener');
    };
  }

  function viewMoreClickHandler() {
    window.open(marketsMap[activeTab].viewMoreLink, '_self', 'noopener');
  }
</script>

<style>
  .outer {
    background: #fff;
  }
  .wrap {
    margin: 0 auto;
    padding-bottom: 40px;
    width: 1200px;
  }
  .caption {
    margin-top: 0;
  }
  .content {
    border: 1px solid #edeff3;
  }
  .tab {
    margin-top: 0;
    margin-bottom: 0;
    border-bottom: 2px solid transparent;
    line-height: 24px;
    color: #010612;
  }
  .tab.active {
    border-color: #003fef;
  }
  .container {
    display: grid;
    grid-template-columns: auto max-content max-content max-content max-content;
  }
  .row {
    display: contents;
    line-height: 64px;
  }
  .row:hover .cell,
  .crypto:hover {
    background: #f6f7fb;
  }
  .cell {
    padding-left: 40px;
  }
  .cell:first-child {
    padding-left: 24px;
  }
  .cell:last-child {
    padding-right: 24px;
  }
  /* 還沒有讀取到資料時，預設要是黑字，不是綠字 */
  .up {
    color: #40c9a6;
  }
  .iconfont {
    transform: rotate(270deg) translateX(-1px);
  }
  .right {
    border-left: 1px solid #edeff3;
    width: 382px;
  }
  .more-wrap + :global(.more-wrap) {
    border-top: 1px solid #edeff3;
  }
  .more-wrap .title-wrap {
    height: 38px;
  }
  .crypto {
    height: 64px;
  }
  .crypto:last-child {
    margin-bottom: 0;
  }
  .label {
    margin: 0;
  }
  .view-more-btn-wrap {
    margin-right: -32px;
  }
  .view-more-btn-wrap :global(button) {
    line-height: 38px;
  }
  @media screen and (max-width: 640px) {
    .wrap {
      padding-bottom: 8px;
      width: 100%;
    }
    .caption {
      margin-bottom: 24px;
      margin-left: 16px;
    }
    .up {
      padding: 12px 8px;
      background: #40c9a6;
      color: white;
    }
    .down {
      padding: 12px 8px;
      background: var(--sell);
      color: white;
    }
    .content {
      border: 0;
    }
    .data-wrap {
      padding: 0;
    }
    .tab {
      flex: 0 0 auto;
      margin-left: 16px;
      font-size: 14px;
      color: #777b83;
    }
    .tab:first-child {
      margin-left: 0;
    }
    .tab.active {
      color: #010612;
    }
    .tab-wrap {
      margin: 0 -8px;
      overflow: auto;
    }
    .tab-wrap::-webkit-scrollbar {
      width: 0;
    }
    .row {
      display: contents;
      line-height: 60px;
    }
    .container {
      margin: 0 auto;
      border: none;
      padding: 0 14px;
      width: unset;
      grid-template-columns: auto auto auto;
    }
    .row .cell {
      padding-left: 0;
    }
    .cell:first-child {
      font-size: 14px;
    }
    .cell:nth-child(2),
    .cell:nth-child(3) {
      font-size: 16px;
    }
    .row .cell:nth-child(4),
    .row .cell:last-child {
      display: none;
    }
    .cell-type {
      color: var(--T5);
    }
    .data-wrap :global(.h5-more) {
      margin: 0 auto;
    }
  }
</style>

<section class="outer">
  <div class="wrap">
    <h2 class="caption mb32 f32">{LG(lang.CryptoPrices)}</h2>
    <div class="content df br12 fw2">
      <div class="data-wrap f1 pv24">
        <div class="df aic jcsb ph24">
          <div class="tab-wrap df aic" on:click={tabClickHandler}>
            <h3 class="tab f16 cp" class:active={activeTab === TAB_TYPE.SPOT} data-tab={TAB_TYPE.SPOT}>{LG(lang.HotSpot)}</h3>
            <h3 class="tab ml24 f16 cp" class:active={activeTab === TAB_TYPE.CONTRACT} data-tab={TAB_TYPE.CONTRACT}>{LG(lang.HotContract)}</h3>
            <h3 class="tab ml24 f16 cp dn xs:db" class:active={activeTab === TAB_TYPE.GAINER} data-tab={TAB_TYPE.GAINER}>{LG(lang.TopGainers)}</h3>
            <h3 class="tab ml24 f16 cp dn xs:db" class:active={activeTab === TAB_TYPE.NEW_LISTING} data-tab={TAB_TYPE.NEW_LISTING}>{LG(lang.NewListhings)}</h3>
          </div>
          <div class="view-more-btn-wrap f14 xs:dn">
            <Button type="text" on:click={viewMoreClickHandler}>{LG(lang.ViewMore)}<i class="iconfont arrow dib fw3">&#xe623;</i></Button>
          </div>
        </div>
        <SkeletonBasicView loaded={$hotData$ && $hotData$[activeTab] && $hotData$[activeTab].length === HOT_DATA_LIST_COUNT}>
          <svelte:fragment slot="skeleton">
            <div class="df fdcr ml24 mr24 mr12">
              {#each new Array(HOT_DATA_LIST_COUNT).fill(0) as item}
                <SkeletonLine class="mt12" height="64px" childProps={!$isMobileSize$ ? [
                  {width: '32px', height: '32px', borderRadius: '50%'},
                  {width: '180px'},
                  {width: '120px', class: 'grid-end'},
                  {width: '60px', class: 'grid-end'},
                  {width: '100px', class: 'grid-end'},
                  {width: '70px', class: 'grid-end'},
                ] : [
                  {width: '180px', borderRadius: '4px'},
                  {width: '100px', class: 'grid-end'},
                  {width: '70px', class: 'grid-end'},
                ]}></SkeletonLine>
              {/each}
            </div>
          </svelte:fragment>
          <div class="container">
            {#if $hotData$ && $hotData$[activeTab]}
              {#each $hotData$[activeTab] as data}
                <div class="row aic" on:click={tradeClickHandler(data)}>
                  <div class="cell mt12 df aic jcfs fw2">
                    <img class="mr12 xs:dn" width="32" height="32" src={coinUtil.getUrl(data.baseCurrency)} alt={data.displaySymbol} />
                    {#if data.type === 'Perpetual' || data.type === 'PerpetualV2'}
                      <div>
                        <div class="lh20 fw2 ooo">{data.symbolV2} <span class="cell-type">{LG(lang.Perpetual)}</span></div>
                        <div class="lh20 f12 T4">{data.settleCurrency}-{LG(lang.Margin)}</div>
                      </div>
                    {:else}
                      {data.displaySymbol}
                    {/if}
                  </div>
                  <div class="cell mt12 f18 tr">$ {data.close}</div>
                  <div class="cell mt12 f18 tr">
                  <span class:up={data.changePercent > 0} class:down={data.changePercent < 0} class="br4">
                    {data.changePercentStr}
                  </span>
                  </div>
                  <div class="cell mt12 tr xs:dn">
                    <TrendImage width={100} height={22} data={data.klineData} change={data.change24hEp} />
                  </div>
                  <div class="cell mt12 tr xs:dn">
                    <Button class="trade wsn fw2" newRadius type="common" size="small">{LG(lang.Trade)}</Button>
                  </div>
                </div>
              {/each}
            {/if}
          </div>
        </SkeletonBasicView>
        <div class="h5-more mt16 tc f14 dn xs:db">
          <Button type="text" on:click={viewMoreClickHandler}>View More <i class="iconfont arrow dib fw3">&#xe623;</i></Button>
        </div>
      </div>
      <div class="right f16 xs:dn">
        {#each more as m}
          <div class="more-wrap pv24">
            <div class="title-wrap df aic jcsb ph24">
              <h3 class="label">{m.label}</h3>
              <a href={m.href} class="f14" ref="noopener">All <i class="iconfont arrow dib fw3">&#xe623;</i></a>
            </div>
            <div>
            <SkeletonBasicView loaded={m.cryptos && m.cryptos.length === MORE_DATA_LIST_COUNT}>
              <svelte:fragment slot="skeleton">
                <div class="df fdcr ml24 mr24">
                  {#each new Array(MORE_DATA_LIST_COUNT).fill(0) as item}
                    <SkeletonLine class="mt12" height="64px" childProps={[
                      {width: '32px', height: '32px', borderRadius: '50%'},
                      {width: '200px'},
                      {width: '60px', class: 'grid-end'},
                    ]}></SkeletonLine>
                  {/each}
                </div>
              </svelte:fragment>
              {#each m.cryptos as crypto}
                <div class="crypto mt12 df aic jcsb ph24 cp" on:click={tradeClickHandler(crypto)}>
                  <div class="df aic">
                    <img width="32" height="32" src={coinUtil.getUrl(crypto.baseCurrency)} alt={crypto.displaySymbol} />
                    <div class="ml12">
                      <div>{crypto.displaySymbol} {crypto.type !== 'Spot' ? LG(cLang.Perpetual) : ''}</div>
                    </div>
                  </div>
                  <div class="tr">
                    <div>{crypto.close}</div>
                    <div class={crypto.changePercent > 0 ? 'up' : 'down'}>{crypto.changePercentStr}</div>
                  </div>
                </div>
              {/each}
            </SkeletonBasicView>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </div>
</section>
